import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useThemePreferences } from '@/hooks/useThemePreferences'
import { cn } from '@/lib/utils'
import { SIDEBAR_THEMES } from '@/store/ui-preferences.store'
import {
  CheckIcon,
  MoonIcon,
  PaletteIcon,
  SunIcon,
} from '@phosphor-icons/react'

import { SidebarTheme } from '@scrambl/shared'

interface ModeToggleProps {
  showSidebarTheme?: boolean
  showGlassToggle?: boolean
}

export function ModeToggle({ showSidebarTheme = true }: ModeToggleProps) {
  const {
    themeMode,
    sidebarTheme,
    currentSidebarColor,
    setThemeMode,
    setSidebarTheme,
  } = useThemePreferences()

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 rounded-full transition-colors hover:bg-muted/50"
          aria-label="Toggle theme"
        >
          <SunIcon className="transition-tonal h-4 w-4 rotate-0 scale-100 dark:-rotate-90 dark:scale-0" />
          <MoonIcon className="transition-tonal absolute h-4 w-4 rotate-90 scale-0 dark:rotate-0 dark:scale-100" />
          <span className="sr-only">Toggle theme</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[180px]">
        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
          Appearance
        </DropdownMenuLabel>
        <DropdownMenuItem
          onClick={() => setThemeMode('light')}
          className={themeMode === 'light' ? 'bg-accent/50' : ''}
        >
          <SunIcon className="mr-2 h-4 w-4" />
          Light
          {themeMode === 'light' && <CheckIcon className="ml-auto h-4 w-4" />}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => setThemeMode('dark')}
          className={themeMode === 'dark' ? 'bg-accent/50' : ''}
        >
          <MoonIcon className="mr-2 h-4 w-4" />
          Dark
          {themeMode === 'dark' && <CheckIcon className="ml-auto h-4 w-4" />}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => setThemeMode('system')}
          className={themeMode === 'system' ? 'bg-accent/50' : ''}
        >
          <div className="mr-2 flex h-4 w-4 items-center justify-center">
            <div className="h-3 w-3 rounded-full border-2 border-current" />
          </div>
          System
          {themeMode === 'system' && <CheckIcon className="ml-auto h-4 w-4" />}
        </DropdownMenuItem>

        {showSidebarTheme && (
          <>
            <DropdownMenuSeparator />

            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <PaletteIcon className="mr-2 h-4 w-4" />
                <span>Theme</span>
                <div
                  className="ml-auto h-4 w-4 overflow-hidden rounded-full border border-border shadow-sm"
                  style={{ backgroundColor: currentSidebarColor }}
                />
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="min-w-[180px]">
                {SIDEBAR_THEMES.map(themeOption => (
                  <DropdownMenuItem
                    key={themeOption.id}
                    onClick={() =>
                      setSidebarTheme(themeOption.id as SidebarTheme)
                    }
                    className={cn(
                      'flex items-center gap-2',
                      sidebarTheme === themeOption.id && 'bg-accent/50',
                    )}
                  >
                    {/* Show both light and dark colors side by side */}
                    <div className="flex h-5 w-5 flex-shrink-0 overflow-hidden rounded-full border border-border shadow-sm">
                      <div
                        className="h-full w-1/2"
                        style={{ backgroundColor: themeOption.lightColor }}
                      />
                      <div
                        className="h-full w-1/2"
                        style={{ backgroundColor: themeOption.darkColor }}
                      />
                    </div>
                    <span className="text-sm">{themeOption.name}</span>
                    {sidebarTheme === themeOption.id && (
                      <CheckIcon className="ml-auto h-4 w-4" />
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
